'use client';

import type { Collaborator } from '@/app/models/collaborator';
import type { Project } from '@/app/models/project';
import { useSearchPersonQuery } from '@/app/services/personApi';
import { useSearchProjectsQuery } from '@/app/services/projectApi';
import GenericAutoComplete from '@/components/common/GenericAutoComplete';
import LateralDrawer from '@/components/common/LateralDrawer';
import PageTitle from '@/components/common/pageTitle';
import TextButton from '@/components/common/TextButton';
import HistoricTable from '@/components/Historic/HistoricTable';
import UploadHistoricFile from '@/components/Historic/UploadHistoricFile';
import { Box, Typography, useTheme } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateField } from '@mui/x-date-pickers/DateField';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function UploadHistoric() {
  const theme = useTheme();
  const { t } = useTranslation(['historic', 'finalVersion', 'common']);
  const [drawerStatus, setDrawerStatus] = useState(false);

  const [originProject, setOriginProject] = useState<Project | null>(null);
  const [originProjectSearch, setOriginProjectSearch] = useState('');
  const { data: originProjects, isFetching: isLoadingOrigin } = useSearchProjectsQuery({
    searchTerm: originProjectSearch,
    bringClosed: false,
    page: 0,
    size: 10,
    sortKey: 'name',
    sortDirection: 'ASC',
  });

  const [destinationProject, setDestinationProject] = useState<Project | null>(null);
  const [destinationProjectSearch, setDestinationProjectSearch] = useState('');
  const { data: destinationProjects, isFetching: isLoadingDestination } = useSearchProjectsQuery({
    searchTerm: destinationProjectSearch,
    bringClosed: false,
    page: 0,
    size: 10,
    sortKey: 'name',
    sortDirection: 'ASC',
  });

  const [collaborator, setCollaborator] = useState<Collaborator | null>(null);
  const [collaboratorSearch, setCollaboratorSearch] = useState('');
  const { data: collaborators, isFetching: isLoadingCollaborators } = useSearchPersonQuery(
    {
      searchTerm: collaboratorSearch,
      available: true,
      page: 0,
      size: 10,
      sortKey: 'name',
      sortDirection: 'ASC',
    },
    { skip: collaboratorSearch.length < 1 },
  );

  const [period, setPeriod] = useState<string | null>(
    dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
  );

  const filterProps = {
    originProject,
    destinationProject,
    collaborator,
    period,
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        mr: 2,
      }}
    >
      <PageTitle
        title={t('historic.title')}
        titleLabel={` (${t('historic.indirect.sharing')})`}
        subtitle={t('page.description') as string}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          mb: 3,
          gap: 2,
          flexWrap: 'wrap',
          alignItems: 'end',
        }}
      >
        <Box sx={{ flex: 1, minWidth: theme.spacing(31) }}>
          <Typography component="p" variant="fieldHeader">
            {t('common:period')}
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateField
              variant="outlined"
              size="small"
              defaultValue={dayjs().subtract(1, 'month')}
              format="MM-YYYY"
              onChange={(newValue, context) => {
                if (context.validationError !== null) return;
                setPeriod(newValue ? newValue?.startOf('M').format('YYYY-MM-DD') : null);
              }}
            />
          </LocalizationProvider>
        </Box>

        <TextButton
          sx={{ ml: 'auto' }}
          text={`+ ${t('upload.historic')}`}
          onClick={() => {
            setDrawerStatus(true);
          }}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          mb: 3,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ flex: 1, minWidth: theme.spacing(31) }}>
          <Typography component="p" variant="fieldHeader">
            {t('finalVersion:origin.project')}
          </Typography>
          <GenericAutoComplete
            value={originProject}
            onChange={(value) => {
              setOriginProject(value);
            }}
            equalityTest={(option, value) => option?.code === value?.code}
            getOptionLabel={(option) =>
              `${option?.code} - ${option?.name}${option?.branchName ? ` (${option?.branchName})` : ''}`
            }
            onSearch={(value) => {
              setOriginProjectSearch(value);
            }}
            options={originProjectSearch !== '' && originProjects?.content ? originProjects?.content : []}
            placeholder={t('search.project') || ''}
            loading={isLoadingOrigin}
            sx={{ width: theme.spacing(50) }}
          />
        </Box>

        <Box sx={{ flex: 1, minWidth: theme.spacing(31) }}>
          <Typography component="p" variant="fieldHeader">
            {t('finalVersion:destination.project')}
          </Typography>
          <GenericAutoComplete
            value={destinationProject}
            onChange={(value) => {
              setDestinationProject(value);
            }}
            equalityTest={(option, value) => option?.code === value?.code}
            getOptionLabel={(option) =>
              `${option?.code} - ${option?.name}${option?.branchName ? ` (${option?.branchName})` : ''}`
            }
            onSearch={(value) => {
              setDestinationProjectSearch(value);
            }}
            options={
              destinationProjectSearch !== '' && destinationProjects?.content ? destinationProjects?.content : []
            }
            placeholder={t('search.project') || ''}
            loading={isLoadingDestination}
            sx={{ width: theme.spacing(50) }}
          />
        </Box>

        <Box sx={{ flex: 1, minWidth: theme.spacing(31) }}>
          <Typography component="p" variant="fieldHeader">
            {t('finalVersion:collaborator')}
          </Typography>
          <GenericAutoComplete
            value={collaborator}
            onChange={(value) => {
              setCollaborator(value);
            }}
            equalityTest={(option, value) => option?.email === value?.email}
            getOptionLabel={(option) => `${option?.badge} - ${option?.name}`}
            onSearch={(value) => {
              setCollaboratorSearch(value);
            }}
            options={collaboratorSearch !== '' && collaborators?.content ? collaborators?.content : []}
            placeholder={t('search.collaborator') || ''}
            loading={isLoadingCollaborators}
            sx={{ width: theme.spacing(50) }}
          />
        </Box>
      </Box>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <HistoricTable {...filterProps} />
      <LateralDrawer open={drawerStatus} setOpen={setDrawerStatus} position="right" size="bigger">
        <UploadHistoricFile setDrawerOpen={setDrawerStatus} />
      </LateralDrawer>
    </Box>
  );
}
